// Utility functions for data export and management

export const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;

  // Get all unique keys from the data
  const keys = Array.from(new Set(data.flatMap(obj => Object.keys(obj))));
  
  // Create CSV header
  const csvHeader = keys.join(',');
  
  // Create CSV rows
  const csvRows = data.map(row => {
    return keys.map(key => {
      let value = row[key];
      
      // Handle nested objects
      if (typeof value === 'object' && value !== null) {
        value = JSON.stringify(value).replace(/"/g, '""');
      }
      
      // Escape values containing commas or quotes
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        value = `"${value.replace(/"/g, '""')}"`;
      }
      
      return value ?? '';
    }).join(',');
  });
  
  // Combine header and rows
  const csv = [csvHeader, ...csvRows].join('\n');
  
  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToJSON = (data, filename) => {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatCurrency = (amount, currency = '€') => {
  if (typeof amount !== 'number') return amount;
  return `${currency}${amount.toLocaleString()}`;
};

// Venue-specific export formatter
export const exportVenues = (venues) => {
  const exportData = venues.map(venue => ({
    Name: venue.name,
    Type: venue.type,
    Address: venue.address,
    Email: venue.contact?.email || '',
    Phone: venue.contact?.phone || '',
    Capacity: venue.capacity,
    Hours: venue.hours,
    'Sound System': venue.soundSystem,
    Pricing: venue.pricing,
    'Best For': venue.bestFor,
    Website: venue.website || '',
    Social: venue.social || '',
    Notes: venue.notes || ''
  }));
  
  exportToCSV(exportData, `venues_export_${new Date().toISOString().split('T')[0]}.csv`);
};

// Contact-specific export formatter
export const exportContacts = (contacts) => {
  const exportData = contacts.map(contact => ({
    Name: contact.name,
    Role: contact.role || '',
    Company: contact.company || '',
    Email: contact.email || '',
    Phone: contact.phone || '',
    Tags: contact.tags?.join(', ') || '',
    'Last Contact': contact.lastContact || '',
    Notes: contact.notes || ''
  }));
  
  exportToCSV(exportData, `contacts_export_${new Date().toISOString().split('T')[0]}.csv`);
};

// Mailing list export formatter
export const exportMailingList = (subscribers) => {
  const exportData = subscribers.map(sub => ({
    Email: sub.email,
    Status: sub.status,
    Source: sub.source,
    'Date Joined': formatDate(sub.date),
    'GDPR Consent': sub.gdpr ? 'Yes' : 'No'
  }));
  
  exportToCSV(exportData, `mailing_list_export_${new Date().toISOString().split('T')[0]}.csv`);
};
